import { RECURSO_PARA_ASIGNAR } from "./recursosParaAsignar";
import type { ResourceId } from "./resources";

/**
 * Puente INTERINO ruta -> resource id. Existe sólo mientras las páginas todavía
 * gatean por string de ruta (usePermissions/checkIfHasPermission). El destino es
 * que cada página pida su recurso directo con `useCan(RESOURCES.X, action)` y
 * este archivo desaparezca (se hace con la extracción a @trud/auth, #49). Hasta
 * entonces, resuelve el string de ruta al MISMO resource id del entitlement, así
 * el enforcement pasa por `can` (vocab canónico) y NO por `user.paths` (lossy).
 *
 * `satisfies` valida que cada id sea un ResourceId real (atrapa typos).
 */
const ROUTE_TO_RESOURCE = {
  "/chat": "ai/chat",
  "/clientes/administrar": "people/buyers",
  "/clientes/deuda-inicial": "collection/initial-debt",
  "/clinical": "clinical",
  "/clinical/calendar": "clinical",
  "/clinical/certificate-templates": "clinical",
  "/clinical/certificates": "clinical",
  "/clinical/config/turnero": "clinical",
  "/clinical/insurance-plans": "clinical",
  "/clinical/occupancy-resources": "clinical",
  "/clinical/patients": "clinical",
  "/clinical/protocols": "clinical",
  "/clinical/reminders": "clinical",
  "/clinical/stays": "clinical",
  "/clinical/veterinarians": "clinical",
  "/clinical/veterinary-services": "clinical",
  "/clinical/visits": "clinical",
  "/clinical/waiting-room": "clinical",
  // Asignar un pago o un cobro: el permiso lo fija RECURSO_PARA_ASIGNAR, el mismo
  // que usan el guard de la ruta y los botones «Asignar ahora». El mapa mandaba
  // los pagos a `disbursement/payables` y el guard a `disbursement/disbursements`.
  "/cobranzas/asignar": RECURSO_PARA_ASIGNAR.cobro,
  "/cobranzas/cobranzas": "collection/deposits",
  "/compras/administrar": "purchase/purchases",
  "/compras/compra-inicial": "purchase/initial-debt",
  "/compras/pagos": "disbursement/disbursements",
  "/compras/pagos/asignar": RECURSO_PARA_ASIGNAR.pago,
  "/compras/solicitud-compra": "purchase/stock-order",
  "/cupones/administrar": "coupon/coupons",
  "/inventario/administrar": "inventory/inventories",
  "/inventario/categorias": "catalog/categories",
  // Un grupo es un conjunto de variantes: quien administra productos administra
  // sus grupos. Ver el comentario de la ruta en routes.tsx.
  "/inventario/grupos": "catalog/products",
  "/inventario/locales": "inventory/warehouses",
  "/inventario/productos": "catalog/products",
  "/inventario/transferencia-de-mercaderia": "inventory/transfer",
  "/ofertas/administrar": "offer",
  "/precios/administrar": "pricing/modify-price",
  "/precios/listas": "pricing/price-list",
  "/precios/masivo": "pricing/price-bulk",
  "/precios/planes": "pricing/price-plan",
  "/precios/planes-mayoristas": "pricing/price-wholesaler-plan",
  "/precios/recargos": "pricing/checkout-fees",
  "/precios/templates": "pricing/price-template",
  "/pricing/price-wholesaler-plan": "pricing/price-wholesaler-plan",
  "/proveedores": "supplier/suppliers",
  "/proveedores/marcas": "supplier/brands",
  "/reportes": "report/reports",
  "/monitor": "report/monitor",
  "/reservas/administrar": "reservation/reservations",
  "/reservas/booking": "reservation/bookings",
  "/reservas/recursos": "reservation/reservables",
  "/reservas/reservas": "reservation/bookings",
  "/reservas/servicios": "reservation/services",
  "/reservas/unidad-reservable": "reservation/reservables",
  "/tableros": "boards",
  "/tesoreria/caja": "treasury/cashdesks",
  "/tesoreria/cheques": "treasury/checks",
  "/tesoreria/gastos": "treasury/expenses",
  "/usuarios": "auth/users",
  "/ventas-b2b": "big-supplier/seller",
  "/ventas/ordenes/administrar": "order/orders",
} satisfies Record<string, ResourceId>;

export function resourceForRoute(
  pathname?: string | null
): ResourceId | undefined {
  if (!pathname) return undefined;
  return (ROUTE_TO_RESOURCE as Record<string, ResourceId>)[pathname];
}
